/**
 * Credit-option link tracking (CEL-3431, Nitish Oct 14 2026): the three options an agent shows when credits run out
 * (Auto-Recharge, start a subscription, one-time top-up) are links inside the agent's own words, worded differently in
 * every language. We classify each link by its DESTINATION only, never its text, and hand back data-track attributes the
 * app-wide click layer (appClickTracking.js) already turns into `app_click`. The Stripe links are never changed.
 *
 *   /profile?tab=billing&topup=1&plan=1   -> chat_option_subscription
 *   /profile?tab=billing (no plan)        -> chat_option_auto_recharge
 *   https://buy.stripe.com/...            -> chat_option_topup  (detail = the dollar figure shown in the link, e.g. "25")
 *
 * CEL-3556 (Oct 20 2026): the server tags each Billing-settings link with a harmless `for=` param (the Billing page reads
 * only its own params) so a card or limit click no longer counts as Auto-Recharge; untagged and for=autorecharge keep
 * the old name, so the series stays continuous. The no-sign-in plan link and the team billing page are new destinations.
 *   /profile?tab=billing&for=card         -> chat_option_update_card       (the Auto-Recharge card)
 *   /profile?tab=billing&for=limit        -> chat_option_change_limit      (the Auto-Recharge monthly limit)
 *   /profile?tab=billing&for=plan_card    -> chat_option_update_plan_card  (the subscription's card, plan payment failed)
 *   /api/plan/<token>/<tier>?for=offer    -> chat_option_offer             (detail = the tier, e.g. "basic")
 *   /api/plan/<token>/<tier>              -> chat_option_subscription      (detail = the tier)
 *   /organization?tab=billing             -> chat_option_team_plan
 */
const STRIPE_HOSTS = new Set(['buy.stripe.com']);
const BILLING_FOR = {
  card: 'chat_option_update_card',
  limit: 'chat_option_change_limit',
  plan_card: 'chat_option_update_plan_card',
  autorecharge: 'chat_option_auto_recharge',
};
const PLAN_LINK = /^\/api\/plan\/[A-Za-z0-9_-]+\/([a-z0-9_]+)$/;

function parse(href) {
  try {
    return new URL(href, 'https://cellcog.ai');
  } catch (e) {
    return null;
  }
}

function amountFrom(text) {
  const m = String(text || '').match(/\$\s?(\d{1,4})(?![\d.,]*\d)/);
  return m ? m[1] : null;
}

/** Returns {'data-track', 'data-track-detail'?, 'data-track-surface'} or null for any other link. */
export function creditOptionTrackAttrs(href, linkText) {
  const u = parse(href);
  if (!u) return null;
  let element = null;
  let detail = null;
  if (STRIPE_HOSTS.has(u.hostname)) {
    element = 'chat_option_topup';
    detail = amountFrom(linkText);
  } else if (/(^|\.)cellcog\.ai$/.test(u.hostname) || u.hostname === 'localhost') {
    const plan = PLAN_LINK.exec(u.pathname);
    if (u.pathname === '/profile' && u.searchParams.get('tab') === 'billing') {
      element = u.searchParams.get('plan') === '1'
        ? 'chat_option_subscription'
        : BILLING_FOR[u.searchParams.get('for')] || 'chat_option_auto_recharge';
    } else if (plan && plan[1] !== 'state') {
      element = u.searchParams.get('for') === 'offer' ? 'chat_option_offer' : 'chat_option_subscription';
      detail = plan[1];
    } else if (u.pathname === '/organization' && u.searchParams.get('tab') === 'billing') {
      element = 'chat_option_team_plan';
    }
  }
  if (!element) return null;
  const attrs = { 'data-track': element, 'data-track-surface': 'chat' };
  if (detail) attrs['data-track-detail'] = detail;
  return attrs;
}

/** Plain text of a react-markdown link's children (for the dollar figure only). */
export function childrenText(children) {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenText).join('');
  if (children.props) return childrenText(children.props.children);
  return '';
}
